import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CountriesService, Country, NationalityTaxAndEmploymentInformation, TinErrorPipe, TinPipe } from '@zero/onboarding/domain';
import { IconComponent, InputComponent, Item, SelectComponent } from '@zero/shared/ui-web-design-system';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';

@Component({
  imports: [
    OnboardingContainerComponent,
    TranslocoPipe,
    SelectComponent,
    AsyncPipe,
    NgbTooltipModule,
    NgTemplateOutlet,
    InputComponent,
    TinPipe,
    IconComponent,
    TinErrorPipe,
  ],
  templateUrl: './nationality.component.html',
})
export class NationalityComponent extends BaseComponent {
  readonly #countriesService = inject(CountriesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #nationality = new BehaviorSubject<string>(undefined);
  readonly #taxCountry = new BehaviorSubject<string>(undefined);
  readonly #tin = new BehaviorSubject<string>(undefined);
  readonly #tinPipe = new TinPipe(this.#countriesService);

  readonly #nationalities = new BehaviorSubject<Item[]>([]);
  readonly #taxCountries = new BehaviorSubject<Item[]>([]);

  public readonly nationality$ = this.#nationality.asObservable();
  public readonly tin$ = this.#tin.asObservable();
  public readonly taxCountry$ = this.#taxCountry.asObservable();
  public readonly nationalities$ = this.#nationalities.asObservable();
  public readonly taxCountries$ = this.#taxCountries.asObservable();

  public readonly isFormValid$ = combineLatest([
    this.nationality$,
    this.taxCountry$,
    this.tin$,
    this.store.sendingNationalityTaxInformation$,
  ]).pipe(
    map(([nationality, taxCountry, tin, sendingNationalityTaxInformation]) => {
      return !!nationality && !!taxCountry && !!this.#tinPipe.transform(tin, taxCountry) && !sendingNationalityTaxInformation;
    }),
  );

  public readonly taxCountryName$ = this.taxCountries$.pipe(
    map((countries) => countries.find((country) => country.value === this.#taxCountry.value)),
    map((country) => country?.label),
  );

  protected override populateFromData(): void {
    combineLatest([this.store.selectedChildOnboarding$, this.store.countries$])
      .pipe(
        filter(([data, countries]) => !!data && !!countries?.length),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(([{ steuerid, nationality }, countries]) => {
        this.#taxCountry.next('DE');
        this.#tin.next(steuerid);
        this.#setNationality(countries, nationality);
      });
  }

  protected override listenToSuccessfulSubmit(): void {
    this.store.sentNationalityTaxInformationSuccessfully$
      .pipe(
        filter((sent) => sent),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.service.goToNextStep());
  }

  protected override async submit(): Promise<void> {
    (await firstValueFrom(this.isFormValid$)) &&
      this.store.sendNationalityTaxInformation({
        taxCountry: this.#taxCountry.value,
        tin: this.#tin.value,
        nationality: this.#nationality.value,
      } as NationalityTaxAndEmploymentInformation);
  }

  public selectNationality = (item: Item) => this.#nationality.next(item.value as string);
  public handleTinChange = (tin: string) => this.#tin.next(tin);

  #setNationality(nationalities: Country[], nationality: string) {
    this.#nationalities.next(
      nationalities.map(
        (country: Country, index: number): Item => ({
          value: country.isoCode,
          label: country.fullName,
          selected: !nationality ? index === 0 : country.isoCode === nationality,
        }),
      ),
    );
    this.#nationality.next(nationality || (this.#nationalities.value[0]?.value?.toString() ?? ''));
  }
}
